import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Award, Calendar, MapPin } from "lucide-react";
import PageBanner from './PageBanner';

const awardsData = [
  { id: 1, name: "2022 Outstanding Medium Student Branch", year: 2022, description: "Awarded for outstanding overall performance as a medium-sized student branch, recognizing excellence in activities, leadership, and impact.", image: "/asset/awards/Outstanding.webp", location: "Sousse, Tunisia", category: "Branch Performance" },
  { id: 2, name: "2023 Outstanding Medium Student Branch Award", year: 2023, description: "Recognizes a medium-sized student branch that demonstrated exceptional performance and consistency in its operations and initiatives.", image: "/asset/awards/outscan2023.webp", location: "Hammamet, Tunisia", category: "Branch Performance" },
  { id: 3, name: "2023 Regional Exemplary Student Branch Award", year: 2023, description: "Honors a student branch that stands out at the regional level for exemplary achievements, leadership, and impactful activities.", image: "/asset/awards/Regional.webp", location: "Tunisia", category: "Regional Excellence" },
  { id: 4, name: "Best Poster Award", year: 2024, description: "Awarded for delivering the best poster presentation, highlighting clarity, design quality, and effective communication.", image: "/asset/awards/posterscan.webp", location: "Tsyp12, Hammamet", category: "Presentation Excellence" },
  { id: 10, name: "Rising Star", year: 2024, description: "Awarded to Emna Awadni for her outstanding dedication, leadership, and impactful contributions to the community.", image: "/asset/awards/awadniscan.webp", location: "TSYP12, Hammamet", category: "Emerging Talent" },
  { id: 123, name: "Non-Technical Challenge – First Place", year: 2025, description: "Awarded First Place in the Non-Technical Challenge at WIE ACT 4.0 in Hammamet, Tunisia, in recognition of exceptional creativity, leadership, and outstanding performance.", image: "/asset/awards/nontechwiescan.jpg", location: "WIE ACT 4.0, Hammamet, Tunisia", category: "Leadership & Innovation" },
  { id: 5, name: "Outstanding WIE Student Affinity Group Award", year: 2025, description: "Recognizes an outstanding Women in Engineering (WIE) affinity group for excellence in activities, engagement, and impact.", image: "/asset/awards/wiescan.jpg", location: "TSYP13, Hammamet", category: "Women in Engineering" },
  { id: 6, name: "Outstanding Student Chapter Award (LARGE) — SMC SB Chapter", year: 2025, description: "Awarded to a large student chapter for outstanding performance, leadership, and contributions within its technical community.", image: "/asset/awards/smcscan.webp", location: "TSYP13, Hammamet", category: "Chapter Performance" },
  { id: 7, name: "3rd Place — IES Tunisia SB Chapter of the Year", year: 2025, description: "Recognizes a student chapter ranked third for its performance, achievements, and contributions over the year.", image: "/asset/awards/iesscan.webp", location: "TSYP13, Hammamet", category: "Ranking & Recognition" },
  { id: 8, name: "Rising Star", year: 2025, description: "Awarded to Mohamed Aziz Alimi for his leadership, innovation, and impactful role in advancing student activities and initiatives.", image: "/asset/awards/alimiscan.jpg", location: "TSYP13, Hammamet", category: "Emerging Talent" },
  { id: 9, name: "IEEE Regional Exemplary Student Branch Award", year: 2025, description: "Honors a student branch that demonstrates exemplary performance and distinction at the regional IEEE level.", image: "/asset/awards/rey8.webp", location: "Tunisia", category: "Regional Excellence" },
];

export default function Awards() {
  const [selectedYear, setSelectedYear] = useState(null);

  const years = [...new Set(awardsData.map(a => a.year))].sort().reverse();
  const filteredAwards = selectedYear ? awardsData.filter(a => a.year === selectedYear) : awardsData;

  return (
    <div className="min-h-screen bg-[#fafcff] relative">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: `repeating-linear-gradient(-45deg, rgba(15,105,157,0.02) 0px, rgba(15,105,157,0.02) 1px, transparent 1px, transparent 18px)` }}
      />

      <PageBanner
        title="Awards"
        description="A record of outstanding contributions, excellence, and IEEE recognition earned by the IEEE ISIMS Student Branch."
        Icon={Trophy}
      />

      {/* Filter + Stats Bar */}
      <div className="relative z-10 bg-white border-b border-[#006699]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-5">
          {/* Year Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedYear(null)}
              className={`px-5 py-2 text-xs font-semibold uppercase tracking-widest border transition-all duration-200 ${selectedYear === null ? 'bg-[#006699] border-[#006699] text-white' : 'bg-white border-[#006699]/20 text-[#006699] hover:border-[#006699]'}`}
            >All</button>
            {years.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-5 py-2 text-xs font-semibold uppercase tracking-widest border transition-all duration-200 ${selectedYear === year ? 'bg-[#006699] border-[#006699] text-white' : 'bg-white border-[#006699]/20 text-[#006699] hover:border-[#006699]'}`}
              >{year}</button>
            ))}
          </div>
          {/* Stats */}
          <div className="flex items-center gap-6">
            {[{ label: "Total", val: awardsData.length }, { label: "Years", val: years.length }, { label: "Showing", val: filteredAwards.length }].map((s, i) => (
              <React.Fragment key={s.label}>
                {i > 0 && <div className="w-px h-8 bg-[#006699]/10" />}
                <div className="text-center">
                  <p className="text-xl font-bold text-[#006699]">{s.val}</p>
                  <p className="text-[10px] uppercase tracking-widest text-[#00334d]/50 font-medium">{s.label}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Awards 3-Column Grid */}
      <main className="relative z-10 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <AnimatePresence mode="wait">
            {filteredAwards.length > 0 ? (
              <motion.div
                key={selectedYear ?? 'all'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
              >
                {filteredAwards.map((award, index) => (
                  <motion.div
                    key={award.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, delay: index * 0.06 }}
                    className="group bg-white border border-[#006699]/10 hover:border-[#006699]/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(0,102,153,0.08)] flex flex-col"
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden aspect-[4/3] flex-shrink-0">
                      <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#006699] z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#006699] z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                      <img src={award.image} alt={award.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute top-3 left-3 bg-[#006699] px-3 py-1">
                        <span className="text-white text-xs font-bold tracking-widest">{award.year}</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex flex-col flex-1 p-5 sm:p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Award className="w-3.5 h-3.5 text-[#006699] flex-shrink-0" strokeWidth={2} />
                        <span className="text-[10px] font-bold text-[#006699] uppercase tracking-widest">{award.category}</span>
                      </div>
                      <h3 className="text-sm font-bold text-[#00334d] leading-snug mb-3 flex-1">{award.name}</h3>
                      <p className="text-xs text-slate-500 leading-relaxed mb-4 line-clamp-3">{award.description}</p>
                      <div className="flex items-center gap-4 pt-4 border-t border-[#006699]/10">
                        <div className="flex items-center gap-1.5 text-[#00334d]/50">
                          <Calendar className="w-3 h-3" strokeWidth={2} />
                          <span className="text-xs font-medium">{award.year}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[#00334d]/50">
                          <MapPin className="w-3 h-3" strokeWidth={2} />
                          <span className="text-xs font-medium truncate">{award.location}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="text-center py-24 border border-[#006699]/10 bg-white"
              >
                <Trophy className="w-12 h-12 text-[#006699]/20 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#00334d] mb-2">No awards found</h3>
                <p className="text-sm text-slate-400 mb-6">Try selecting a different year</p>
                <button onClick={() => setSelectedYear(null)} className="px-6 py-2.5 bg-[#006699] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#004d73] transition-colors">
                  View All
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}